import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { usePagination } from "@/hooks/usePagination";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { throwIfNotOk } from "@/lib/api-error";
import {
  computeAdminPaymentBreakdown,
  formatMoney,
} from "@/lib/admin-payment-breakdown";
import { Pagination } from "@/components/ui/pagination";
import { Money, X } from "@phosphor-icons/react";

import {
  formatDateShort,
  formatTime,
  itemSummary,
  toLocalISODate,
} from "./payout-helpers";
import {
  PayoutFiltersCard,
  buildPayoutParams,
  emptyPayoutFilters,
  hasAnyPayoutFilter,
  type PayoutFilters,
} from "./PayoutFiltersCard";
import { EmptyState } from "./EmptyState";
import { UnpaidSkeleton } from "./Skeletons";
import { PayButton, PayoutAvatar, SelectCheckbox, StatCard } from "./primitives";

import type { UnpaidSession, PaginatedUnpaid } from "@/types";
import type { WithholdingRates } from "@/lib/money/work-contract";

const ITEMS_PER_PAGE = 20;

interface UnpaidSessionsTabProps {
  /** `withholdingRates` stiže sa servera uz listu, za pregled obustava prije potvrde. */
  onOpenPayoutDialog: (sessions: UnpaidSession[], withholdingRates: WithholdingRates | null) => void;
}

export function UnpaidSessionsTab({ onOpenPayoutDialog }: UnpaidSessionsTabProps) {
  const pagination = usePagination<PayoutFilters>(
    { pageSize: ITEMS_PER_PAGE },
    emptyPayoutFilters,
  );
  const page = pagination.page;
  const setPage = pagination.setPage;
  const filters = pagination.filters;

  // Keyed by session id; keeps whole sessions so selection survives pagination.
  const [selected, setSelected] = useState<Map<string, UnpaidSession>>(
    new Map(),
  );

  const unpaidQuery = useQuery({
    queryKey: [
      "admin",
      "payouts",
      "unpaid",
      page,
      filters.therapistId ?? "",
      filters.dateFrom ? toLocalISODate(filters.dateFrom) : "",
      filters.dateTo ? toLocalISODate(filters.dateTo) : "",
    ],
    queryFn: async () => {
      const params = buildPayoutParams(page, ITEMS_PER_PAGE, filters);
      const res = await fetch(`/api/admin/payouts/unpaid?${params}`);
      await throwIfNotOk(res, "Greška pri učitavanju neisplaćenih sesija");
      return res.json() as Promise<PaginatedUnpaid>;
    },
    placeholderData: (prev) => prev,
  });

  const unpaidSessions = unpaidQuery.data?.data ?? [];
  const totalPages = unpaidQuery.data?.totalPages ?? 1;
  const totalUnpaid = unpaidQuery.data?.totalUnpaid ?? 0;
  const totalSessions = unpaidQuery.data?.total ?? 0;

  /* ─── Selection ─── */

  const allVisibleSelected =
    unpaidSessions.length > 0 &&
    unpaidSessions.every((s) => selected.has(s.id));

  const toggleSelect = (session: UnpaidSession) => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(session.id)) next.delete(session.id);
      else next.set(session.id, session);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelected((prev) => {
      const next = new Map(prev);
      for (const s of unpaidSessions) {
        if (allVisibleSelected) next.delete(s.id);
        else next.set(s.id, s);
      }
      return next;
    });
  };

  const selectedSessions = useMemo(() => [...selected.values()], [selected]);
  const selectedTotal = selectedSessions.reduce(
    (sum, s) => sum + (s.therapistEarnings ?? 0),
    0,
  );
  const selectedTherapists = new Set(selectedSessions.map((s) => s.therapistId))
    .size;

  const hasFilters = hasAnyPayoutFilter(filters);
  const clearFilters = pagination.resetAll;

  return (
    <div className="space-y-4 sm:space-y-5">
      {/* Stats */}
      <div className="grid grid-cols-2 gap-3">
        <StatCard
          label="Ukupno neisplaćeno"
          value={formatMoney(totalUnpaid)}
          suffix="KM"
          tone="primary"
        />
        <StatCard label="Neisplaćenih stavki" value={String(totalSessions)} />
      </div>

      <PayoutFiltersCard
        filters={filters}
        onFilterChange={pagination.setFilter}
        onClear={clearFilters}
      />

      {/* Content */}
      {unpaidQuery.isLoading && unpaidSessions.length === 0 ? (
        <UnpaidSkeleton />
      ) : unpaidSessions.length === 0 ? (
        <EmptyState
          icon={Money}
          title="Nema neisplaćenih sesija"
          description={
            hasFilters
              ? "Pokušajte sa drugim filterima."
              : "Sve obavljene sesije i završene grupne terapije su isplaćene."
          }
          hasFilters={hasFilters}
          onClear={clearFilters}
        />
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden overflow-hidden rounded-2xl border border-border/60 bg-card shadow-sm lg:block">
            <table className="w-full table-fixed text-[13px]">
              <colgroup>
                <col className="w-[46px]" />
                <col className="w-[112px]" />
                <col />
                <col />
                <col className="w-[150px]" />
                <col className="w-[110px]" />
                <col className="w-[108px]" />
              </colgroup>
              <thead>
                <tr className="bg-muted/40">
                  <th className="px-4 py-3">
                    <SelectCheckbox
                      checked={allVisibleSelected}
                      onToggle={toggleSelectAll}
                      label="Odaberi sve sesije na ovoj stranici"
                    />
                  </th>
                  <Th align="left">Datum</Th>
                  <Th align="left">Klijent i usluga</Th>
                  <Th align="left">Terapeut</Th>
                  <Th align="right">Naplaćeno</Th>
                  <Th align="right">Zarada</Th>
                  <th className="px-3 py-3" />
                </tr>
              </thead>
              <tbody>
                {unpaidSessions.map((session, i) => {
                  const breakdown = computeAdminPaymentBreakdown(session);
                  const isSelected = selected.has(session.id);

                  return (
                    <tr
                      key={session.id}
                      className={cn(
                        "transition-colors",
                        isSelected ? "bg-primary/[0.05]" : "hover:bg-primary/[0.03]",
                        i !== unpaidSessions.length - 1 &&
                          "border-b border-border/40",
                      )}
                    >
                      <td className="px-4 py-3">
                        <SelectCheckbox
                          checked={isSelected}
                          onToggle={() => toggleSelect(session)}
                          label={`Odaberi sesiju: ${session.clientName}`}
                        />
                      </td>
                      <td className="whitespace-nowrap px-3 py-3">
                        <div className="font-medium leading-tight text-foreground">
                          {formatDateShort(session.scheduledAt)}
                        </div>
                        <div className="mt-0.5 text-[11px] text-muted-foreground">
                          {session.kind === "enrollment"
                            ? "kraj ciklusa"
                            : formatTime(session.scheduledAt)}
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-2.5">
                          <PayoutAvatar
                            name={session.clientName}
                            className="size-7 text-[10px]"
                          />
                          <div className="min-w-0">
                            <div className="truncate font-medium leading-tight text-foreground">
                              {session.clientName}
                            </div>
                            <div className="mt-0.5 truncate text-[11px] text-muted-foreground">
                              {session.serviceName || "Bez naziva usluge"}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-2.5">
                          <PayoutAvatar
                            name={session.therapistName}
                            tone="primary"
                            className="size-7 text-[10px]"
                          />
                          <span className="truncate font-medium text-foreground">
                            {session.therapistName}
                          </span>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-3 py-3 text-right">
                        <div className="tabular-nums text-foreground">
                          {formatMoney(breakdown.clientPaid)}
                          {breakdown.clientPaid != null && (
                            <span className="ml-0.5 text-[11px] text-muted-foreground">
                              KM
                            </span>
                          )}
                        </div>
                        {(breakdown.vat != null ||
                          breakdown.platformFee != null) && (
                          <div className="mt-0.5 text-[11px] tabular-nums text-muted-foreground">
                            PDV {formatMoney(breakdown.vat)}
                            <span className="mx-1 text-muted-foreground/50">·</span>
                            Prov. {formatMoney(breakdown.platformFee)}
                          </div>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-3 py-3 text-right">
                        <span className="font-semibold tabular-nums text-foreground">
                          {formatMoney(breakdown.therapistEarnings)}
                          {breakdown.therapistEarnings != null && (
                            <span className="ml-0.5 text-[11px] font-normal text-muted-foreground">
                              KM
                            </span>
                          )}
                        </span>
                      </td>
                      <td className="py-3 pl-1 pr-4 text-right">
                        <PayButton onClick={() => onOpenPayoutDialog([session], unpaidQuery.data?.withholdingRates ?? null)} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile/Tablet: select-all bar + cards */}
          <div className="lg:hidden">
            <div
              role="button"
              tabIndex={0}
              onClick={toggleSelectAll}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  toggleSelectAll();
                }
              }}
              className="mb-3 inline-flex cursor-pointer items-center gap-2.5 rounded-xl border border-border/60 bg-card px-3.5 py-2.5 text-sm font-medium text-foreground shadow-sm transition-colors hover:border-primary/30"
            >
              <SelectCheckbox
                checked={allVisibleSelected}
                onToggle={toggleSelectAll}
                label="Odaberi sve sesije na ovoj stranici"
              />
              Odaberi sve na stranici
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              {unpaidSessions.map((session) => {
                const breakdown = computeAdminPaymentBreakdown(session);
                const isSelected = selected.has(session.id);

                return (
                  <div
                    key={session.id}
                    className={cn(
                      "rounded-2xl border border-border/60 bg-card p-4 shadow-sm transition-shadow hover:shadow-md",
                      isSelected && "ring-2 ring-primary/30",
                    )}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-medium leading-tight text-foreground">
                          {formatDateShort(session.scheduledAt)}
                          <span className="ml-1.5 text-xs font-normal text-muted-foreground">
                            {session.kind === "enrollment"
                              ? "kraj ciklusa"
                              : formatTime(session.scheduledAt)}
                          </span>
                        </div>
                      </div>
                      <SelectCheckbox
                        checked={isSelected}
                        onToggle={() => toggleSelect(session)}
                        label={`Odaberi sesiju: ${session.clientName}`}
                        className="size-5"
                      />
                    </div>

                    <div className="mt-3 space-y-2">
                      <div className="flex items-center gap-2">
                        <PayoutAvatar
                          name={session.clientName}
                          className="size-6 text-[9px]"
                        />
                        <span className="truncate text-sm font-medium text-foreground">
                          {session.clientName}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <PayoutAvatar
                          name={session.therapistName}
                          tone="primary"
                          className="size-6 text-[9px]"
                        />
                        <span className="truncate text-sm font-medium text-foreground">
                          {session.therapistName}
                        </span>
                      </div>
                    </div>

                    <div className="mt-2 truncate text-xs text-muted-foreground">
                      {session.serviceName}
                    </div>

                    <div className="mt-3 border-t border-border/40 pt-3">
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <MobileMoneyCell label="Klijent" value={breakdown.clientPaid} />
                        <MobileMoneyCell label="PDV" value={breakdown.vat} muted />
                        <MobileMoneyCell
                          label="Provizija"
                          value={breakdown.platformFee}
                          muted
                        />
                        <MobileMoneyCell
                          label="Zarada"
                          value={breakdown.therapistEarnings}
                        />
                      </div>
                      <div className="mt-3 flex justify-end">
                        <PayButton
                          onClick={() => onOpenPayoutDialog([session], unpaidQuery.data?.withholdingRates ?? null)}
                          className="h-8 text-xs"
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
        </>
      )}

      {/* Floating selection dock */}
      {selected.size > 0 && (
        <div className="pointer-events-none fixed inset-x-0 bottom-4 z-40 px-4 sm:bottom-6">
          <div className="animate-payout-dock pointer-events-auto mx-auto flex w-full max-w-xl flex-wrap items-center gap-x-4 gap-y-2 rounded-2xl border border-primary/25 bg-card px-4 py-3 shadow-xl shadow-primary/10">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-foreground">
                {itemSummary(selectedSessions)}
                <span className="mx-1.5 text-muted-foreground/50">·</span>
                <span className="font-semibold tabular-nums">
                  {formatMoney(selectedTotal)} KM
                </span>
              </p>
              {selectedTherapists > 1 && (
                <p className="mt-0.5 text-[11px] text-muted-foreground">
                  {selectedTherapists} terapeuta, kreiraju se odvojene isplate
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setSelected(new Map())}
                aria-label="Poništi odabir"
                className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <X className="size-4" />
              </button>
              <Button size="sm" onClick={() =>
                  onOpenPayoutDialog(selectedSessions, unpaidQuery.data?.withholdingRates ?? null)
                }>
                <Money className="size-4" />
                Isplati odabrane
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Cells ─── */

function Th({
  children,
  align,
}: {
  children: React.ReactNode;
  align: "left" | "right";
}) {
  return (
    <th
      className={cn(
        "px-3 py-2.5 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground",
        align === "right" ? "text-right" : "text-left",
      )}
    >
      {children}
    </th>
  );
}

function MobileMoneyCell({
  label,
  value,
  muted = false,
}: {
  label: string;
  value: number | null | undefined;
  muted?: boolean;
}) {
  return (
    <div>
      <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div
        className={cn(
          "mt-0.5 tabular-nums",
          muted ? "text-muted-foreground" : "font-semibold text-foreground",
        )}
      >
        {formatMoney(value)}
        {value != null && (
          <span className="ml-0.5 text-[10px] font-normal text-muted-foreground">
            KM
          </span>
        )}
      </div>
    </div>
  );
}
